"use client";

import { useSelector } from "react-redux";

/**
 * Custom hook to check if the current user has specific permission(s)
 * @param {string|string[]} requiredPermissions - Single permission or array of permissions
 * @param {boolean} requireAll - If true, user must have ALL permissions. If false, user needs ANY one.
 * @returns {boolean} Whether the user has the required permission(s)
 */
export function usePermission(requiredPermissions, requireAll = false) {
    const { user } = useSelector((state) => state.auth);

    // If no user, no permission
    if (!user) return false;

    // Admin role has all permissions
    if (user.role?.name?.toLowerCase() === 'admin') return true;

    // Handle permissions whether they are a string (from DB/JSON) or already parsed array
    let userPermissions = [];
    try {
        if (typeof user.role?.permissions === 'string') {
            userPermissions = JSON.parse(user.role.permissions);
        } else if (Array.isArray(user.role?.permissions)) {
            userPermissions = user.role.permissions;
        }
    } catch (e) {
        console.error("Failed to parse permissions in usePermission hook", e);
        return false;
    }

    const permissions = Array.isArray(requiredPermissions)
        ? requiredPermissions
        : [requiredPermissions];

    if (requireAll) {
        // User must have ALL required permissions
        return permissions.every(p => userPermissions.includes(p));
    } else {
        // User needs at least ONE of the required permissions
        return permissions.some(p => userPermissions.includes(p));
    }
}

/**
 * Check if user has a specific permission
 * @param {object} user - User object from auth state
 * @param {string} permission - Permission to check
 * @returns {boolean} Whether the user has the permission
 */
export function hasPermission(user, permission) {
    if (!user) return false;
    if (user.role?.name?.toLowerCase() === 'admin') return true;

    // Handle permissions whether they are a string (from DB/JSON) or already parsed array
    let userPermissions = [];
    try {
        if (typeof user.role?.permissions === 'string') {
            userPermissions = JSON.parse(user.role.permissions);
        } else if (Array.isArray(user.role?.permissions)) {
            userPermissions = user.role.permissions;
        }
    } catch (e) {
        console.error("Failed to parse permissions in hasPermission", e);
        return false;
    }

    return userPermissions.includes(permission);
}

export default usePermission;
